import React, { createContext, useContext, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import type { BulkEditAction, BulkEditState } from './types';

/**
 * Bulk Edit module store.
 *
 * State and dispatch live in SEPARATE contexts so components that only dispatch
 * (e.g. future toolbar actions) do not re-render when unrelated state changes.
 * `dispatch` from useReducer is referentially stable, so its consumers never
 * re-render on state updates — this keeps the module render-safe as it grows.
 */

const initialState: BulkEditState = {
  selectedModule: null,
  selectedSupplier: null,
  selectedProductIds: [],
  designSearchText: '',
};

/**
 * Bulk Edit page reducer.
 *
 * `SET_MODULE` also resets the supplier and the design-details search text: both are
 * module-scoped header state and must not leak across a module change.
 * @param {BulkEditState} state current page state
 * @param {BulkEditAction} action dispatched action
 * @returns {BulkEditState} next state
 */
const bulkEditReducer = (state: BulkEditState, action: BulkEditAction): BulkEditState => {
  switch (action.type) {
    case 'SET_MODULE':
      /** Supplier + design-details header state are module-scoped — clear on module change. */
      return {
        ...state,
        selectedModule: action.payload,
        selectedSupplier: null,
        designSearchText: '',
      };
    case 'SET_SUPPLIER':
      return { ...state, selectedSupplier: action.payload };
    case 'SET_PRODUCT_IDS':
      return { ...state, selectedProductIds: action.payload };
    case 'SET_DESIGN_SEARCH':
      return { ...state, designSearchText: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const BulkEditStateContext = createContext<BulkEditState | undefined>(undefined);
const BulkEditDispatchContext = createContext<React.Dispatch<BulkEditAction> | undefined>(undefined);

interface BulkEditProviderProps {
  children: React.ReactNode;
}

/** Shape of the router location state passed when opening the page. */
interface BulkEditLocationState {
  selectedIds?: string[];
}

const BulkEditProvider: React.FC<BulkEditProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigationState = location.state as BulkEditLocationState | null;
  const seededProductIds: string[] = navigationState?.selectedIds ?? [];

  const [state, dispatch] = useReducer(bulkEditReducer, {
    ...initialState,
    selectedProductIds: seededProductIds,
  });

  return (
    <BulkEditStateContext.Provider value={state}>
      <BulkEditDispatchContext.Provider value={dispatch}>{children}</BulkEditDispatchContext.Provider>
    </BulkEditStateContext.Provider>
  );
};

/** Read the Bulk Edit state. Throws if used outside the provider. */
export const useBulkEditState = (): BulkEditState => {
  const context = useContext(BulkEditStateContext);
  if (context === undefined) {
    throw new Error('useBulkEditState must be used within a BulkEditProvider');
  }
  return context;
};

/** Get the Bulk Edit dispatch. Throws if used outside the provider. */
export const useBulkEditDispatch = (): React.Dispatch<BulkEditAction> => {
  const context = useContext(BulkEditDispatchContext);
  if (context === undefined) {
    throw new Error('useBulkEditDispatch must be used within a BulkEditProvider');
  }
  return context;
};

export default BulkEditProvider;
